import React, {useEffect, useRef, useCallback} from 'react';
import {
    Box,
    Stack,
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    CircularProgress,
} from '@mui/material';
import {
    ChevronLeft,
    Save as SaveIcon,
} from '@mui/icons-material';
import {MyButton, MyIconButton, MyTypography, MySlider} from '../../common/components';
import MyPdfViewer from '../../common/components/MyPdfViewer';
import CreateDetectedReferralForm, {useCreateReferralForm, initialValues} from './CreateDetectedReferralForm';
import Swal from 'sweetalert2';
import {min, max} from 'lodash';
import useNavigatePdfPages from './useNavigatePdfPages';
import {ClinicDropdownDialogProvider} from '../../clinics/components/ClinicDropdownHookFormSelect';
import {ProviderDropdownDialogProvider} from './ReferringProviderOcrAnnotation';

function ReferralPdfPagePicker({startPage, endPage, pageCount, onPageChanged, disabled=false, sx={}}) {
    return (
        <Stack direction="column"
               sx={{pt: 3, pb: 1, px: 4, ...sx}}>
            {/* Page Range Slider */}
            <MySlider
                value={[startPage, endPage]}
                onChange={(_, newValue) => {
                    const firstPage = parseInt(newValue[0]);
                    const lastPage = parseInt(newValue[1]);

                    if (firstPage <= lastPage) {
                        onPageChanged(firstPage, lastPage);
                    }
                }}
                disabled={disabled}
                min={1}
                max={pageCount}
                step={1}
                disableSwap
                valueLabelFormat={(value) => `Page ${value}`}
                valueLabelDisplay="on"
            />
            <MyTypography variant="caption">Note: If a fax includes two or more referrals, use the slider bar to select and save each section of the fax as needed.</MyTypography>
        </Stack>
    );
}

export default function CreateDetectedReferralDialog({open, onClose, selectedReferral=null}) {
    const {defaultValues, ocrSuggestedPages, ocrDetails, pdf, fromFax, actions} = selectedReferral;

    const initialStartPage = ocrSuggestedPages ? min(ocrSuggestedPages) : 1;
    const initialEndPage = ocrSuggestedPages ? max(ocrSuggestedPages) : pdf.pageCount;

    const {blobUrl, processing, reset, ...otherPageMeta} = useNavigatePdfPages({
        originalBlobUrl: pdf?.blobUrl,
        pageCount: pdf?.pageCount,
        initialStartPage,
        initialEndPage,
    });

    const formRef = useRef(null);

    const onSuccess = useCallback(async (data) => {
        console.log('onSuccess:data=',data);

        if (data?.save_fax_referral?.id) {
            Swal.fire({
                icon: 'success',
                text: 'Referral created successfully!',
                showConfirmButton: true,
            });

            onClose();
        } else {
            throw new Error("Error sending referral.");
        }
    }, [onClose]);

    const onFailure = useCallback((err) => {
        console.log('onFailure:err=',err);

        Swal.fire({
            icon: 'error',
            text: err.message || 'Error sending referral!',
            showConfirmButton: true,
        });
    }, []);

    const {
        control,
        onSubmit,
        loading,
        errors,
        setValue,
    } = useCreateReferralForm({
        defaultValues: defaultValues
                       ? defaultValues
                       : initialValues,
        onSuccess,
        onFailure,
        actions,
    });

    useEffect(() => {
        if (open === false) {
            reset();
        } else {
            reset(initialStartPage, initialEndPage, pdf?.pageCount, pdf?.blobUrl);
        }
    }, [reset, open, initialStartPage, initialEndPage, pdf]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            fullWidth
        >
            <DialogTitle sx={{color: '#eee'}}>
                <Stack direction="row"
                       justifyContent="space-between"
                       alignItems="center">
                    <Stack direction="row"
                           alignItems="center"
                           spacing={1}>
                        <MyIconButton onClick={onClose}
                                      color="inherit">
                            <ChevronLeft />
                        </MyIconButton>
                        <Stack direction="column">
                            <MyTypography variant="h3" color="inherit">Create Referral</MyTypography>
                            <MyTypography variant="caption" color="text.secondary">
                                {`Fax: ${fromFax}`}
                            </MyTypography>
                        </Stack>
                    </Stack>
                    <Stack direction="row"
                           spacing={1}
                           alignItems="center">
                        <MyButton color="inherit"
                                  variant="text"
                                  fullWidth={false}
                                  onClick={onClose}
                                  sx={{minWidth: 140}}>
                            Cancel
                        </MyButton>
                        <MyButton color="inherit"
                                  variant="outlined"
                                  loading={loading}
                                  startIcon={<SaveIcon />}
                                  fullWidth={false}
                                  onClick={() => {
                                      if (formRef.current) {
                                          formRef.current.dispatchEvent(
                                              new Event('submit', { bubbles: true, cancelable: true })
                                          );
                                      }
                                  }}
                                  sx={{minWidth: 140}}>
                            Save Referral
                        </MyButton>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent
                sx={{
                    px: 2,
                    overflowY: 'hidden',
                }}>
                <Grid container
                      sx={{pt: 0}}>
                    {
                        (pdf && pdf.blobUrl) &&
                        <Grid item
                              xs={12}
                              md={6}
                              lg={7}
                              xl={8}
                              sx={{borderRight: 1, borderColor: 'divider'}}>
                            <Card>
                                <CardContent
                                    component={Stack}
                                    direction="column"
                                    sx={{
                                        position: 'relative',
                                        height: '100%',
                                        maxHeight: 'calc(100vh - 84px)',
                                        overflow: 'hidden',
                                        py: 1,
                                        px: 0,
                                        bgcolor: 'grey.50',
                                    }}
                                >
                                    {
                                        initialEndPage > 1 &&
                                        <ReferralPdfPagePicker
                                            {...otherPageMeta}
                                            disabled={processing}
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                            }}
                                        />
                                    }
                                    <Box
                                        sx={{
                                            mt: '90px',
                                            overflowY: 'auto',
                                            maxHeight: 'calc(100vh - 90px)',
                                        }}
                                    >
                                    {
                                        processing
                                        ? <CircularProgress />
                                        : (
                                            blobUrl
                                            ? <MyPdfViewer
                                                fileUrl={blobUrl}
                                                additionalToolbarChildren={
                                                    <Stack direction="row">
                                                        <MyTypography variant="caption">Page {otherPageMeta.startPage} of {otherPageMeta.endPage} </MyTypography>
                                                    </Stack>
                                                }
                                            />
                                            : null
                                        )
                                    }
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    }

                    <Grid item
                          xs={12}
                          md={6}
                          lg={5}
                          xl={4}>
                        <Card sx={{height: '100%'}}>
                            <CardHeader
                                title="Referral Information"
                                subheader={
                                    <MyTypography variant="body2" color="text.secondary">
                                        Fill out the form using the reference document on the left
                                    </MyTypography>
                                }
                                sx={{
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    bgcolor: 'background.default',
                                    color: 'primary.main'
                                }}
                            />
                            <CardContent
                                sx={{
                                    overflowY: 'auto',
                                    height: '100%',
                                    maxHeight: 'calc(100% - 180px)',
                                }}
                            >
                                <form
                                    ref={formRef}
                                    onSubmit={(e) => {
                                        const pageParams = {
                                            received_fax_id: selectedReferral.receivedFaxId,
                                            start_page: otherPageMeta.startPage,
                                            end_page: otherPageMeta.endPage,
                                        };
                                        onSubmit(e, pageParams)
                                    }}
                                    noValidate
                                    style={{width: '100%'}}>
                                    <ClinicDropdownDialogProvider>
                                        <ProviderDropdownDialogProvider>
                                            <CreateDetectedReferralForm
                                                control={control}
                                                errors={errors}
                                                setValue={setValue}
                                                ocrDetails={ocrDetails}
                                                actions={actions}
                                            />
                                        </ProviderDropdownDialogProvider>
                                    </ClinicDropdownDialogProvider>
                                </form>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}